import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { ReactComponent as ExpandMoreSvg } from 'assets/img/expand-more.svg';
import { DROPDOWN_TYPE } from 'constants/common';
import { formatValue, handleCombinedArr } from 'helpers/utils';
import { useClickOutside, useMountTransition } from 'hooks';
import DropDown from 'components/DropDown';
import TreeViewDropdownItem from '../TreeViewDropdownItem/TreeViewDropdownItem';
import styles from './TreeViewDropdown.module.scss';

const findOptionByValue = (options, value) => {
  for (let i = 0; i < options.length; i++) {
    const option = options[i];
    if (option.value === value) {
      return option;
    }
    if (option.children) {
      const childrenValue = findOptionByValue(option.children, value);
      if (childrenValue) {
        return childrenValue;
      }
    }
  }
  return undefined;
};

const getLabelsByValue = (options, value) => {
  const labels = [];
  for (let i = 0; i < value.length; i++) {
    if (!value[i] || !options.length) {
      break;
    }

    const option = findOptionByValue(options, value[i]);

    if (option && option.label) {
      labels.push(option.label);
    }
  }
  return labels;
};
// eslint-disable-next-line
const getDropdownItems = ({ options, value, setValue, hideDropdownHandler, multiple, valueStoreType, parentValue }) =>
  options.map(option => {
    const hasChildren = !!option.children && option.children.length !== 0;
    const isSelected =
      valueStoreType === DROPDOWN_TYPE.VALUE_STORE.RELATIVE
        ? value[value.length - 1] === option.value
        : value.includes(option.value);

    return (
      <TreeViewDropdownItem
        multiple={multiple}
        valueStoreType={valueStoreType}
        key={option.value}
        itemLabel={option.label}
        itemValue={option.value}
        setValue={setValue}
        onHideDropdown={hideDropdownHandler}
        selected={isSelected}
        parentValue={parentValue}
      >
        {hasChildren &&
          getDropdownItems({
            options: option.children,
            value,
            setValue,
            hideDropdownHandler,
            multiple,
            valueStoreType,
            parentValue: option.value,
          })}
      </TreeViewDropdownItem>
    );
  });

const TreeViewDropdown = ({ placeholder, options, value, setValue, multiple, valueStoreType, icon }) => {
  const [isDropdownShown, setIsDropdownShown] = useState(false);
  const formattedValue = formatValue(value);

  const labels = options ? getLabelsByValue(options, formattedValue) : [];

  const dropdownRef = useRef(null);
  const hasTransitionedIn = useMountTransition(isDropdownShown, 200);

  const toggleDropdownHandler = () => setIsDropdownShown(state => !state);
  const hideDropdownHandler = () => setIsDropdownShown(false);

  const setValueHandler = (newValue, parentValue = formattedValue) => {
    setValue(multiple ? handleCombinedArr(parentValue, newValue) : newValue);
  };

  const handleInputValue = () => {
    if (placeholder === 'Product Type' && Array.isArray(value)) {
      let updatedValue = value.map(el => el.charAt(0).toUpperCase() + el.slice(1));
      return String(updatedValue.join(', '));
    }
    return labels.join(', ') || placeholder;
  };

  useClickOutside(dropdownRef, hideDropdownHandler);

  return (
    <div className={styles.bns3_dropdown} ref={dropdownRef}>
      <div
        className={styles.bns3_dropdown_select}
        role="presentation"
        onClick={toggleDropdownHandler}
        data-testid={`HomeFilterSelect${labels.join(', ') || placeholder}`}
      >
        {icon && <div className={styles.bns3_dropdown_icon}>{icon}</div>}
        <input type="text" value={handleInputValue()} disabled />
        <ExpandMoreSvg />
      </div>
      {!(isDropdownShown || hasTransitionedIn) ? null : (
        <div
          className={cn(styles.bns3_dropdown_list, {
            [styles.in]: hasTransitionedIn,
            [styles.open]: isDropdownShown,
          })}
        >
          <DropDown
            valueStoreType={valueStoreType}
            data={options}
            multiple={multiple}
            isDropdownShown={isDropdownShown}
            currentCategoryID={formattedValue}
            onChange={setValueHandler}
            handleCloseMainDropDown={hideDropdownHandler}
          />
        </div>
      )}
    </div>
  );
};

TreeViewDropdown.defaultProps = {
  multiple: false,
  valueStoreType: DROPDOWN_TYPE.VALUE_STORE.ABSOLUTE,
};

TreeViewDropdown.propTypes = {
  placeholder: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      children: PropTypes.array,
    })
  ),
  value: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  ]),
  setValue: PropTypes.func.isRequired,
  multiple: PropTypes.bool,
  valueStoreType: PropTypes.string,
  icon: PropTypes.node,
};

export default TreeViewDropdown;
